import { Link } from "react-router-dom";
import { Phone, Mail, ChevronRight, Radio } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BRAND } from "@/data/site";

export const Eyebrow = ({ children }) => (
  <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold text-amber-500 mb-3 font-mono">{children}</p>
);

export const SectionHead = ({ eyebrow, title, desc, light = false, as = "h2" }) => {
  const Tag = as;
  return (
    <div className="max-w-3xl mb-10">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Tag className={`text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-display ${light ? "text-slate-900" : "text-slate-50"}`}>{title}</Tag>
      {desc && <p className={`mt-4 text-base leading-relaxed ${light ? "text-slate-600" : "text-slate-400"}`}>{desc}</p>}
    </div>
  );
};

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-slate-400 mb-6">
    {items.map((it, i) => (
      <span key={it.path} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-600" />}
        {i === items.length - 1 ? (
          <span className="text-amber-500/90">{it.name}</span>
        ) : (
          <Link to={it.path} className="hover:text-amber-400 transition-colors">{it.name}</Link>
        )}
      </span>
    ))}
  </nav>
);

export const CtaBand = ({ heading = "Get a Same-Day Security Quote", sub }) => (
  <section data-testid="cta-band" className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-slate-900 via-[#111827] to-slate-900 p-8 sm:p-12 my-14">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 85% 10%, rgba(245,158,11,0.16) 0%, transparent 55%)" }} />
    <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
      <div className="max-w-xl">
        <div className="flex items-center gap-2 text-amber-500 font-mono text-xs uppercase tracking-[0.25em] mb-3">
          <Radio className="h-3.5 w-3.5 animate-pulse" /> 24/7 / 365 Guard Dispatch
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight">{heading}</h2>
        <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
          {sub || "Speak directly with our dispatch desk. No forms, no waiting: a written, itemized quote the same day, anywhere in Canada."}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
        <a
          href={BRAND.phoneHref}
          data-testid="cta-call-button"
          className="inline-flex items-center justify-center gap-2.5 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-slate-950 shadow-lg shadow-amber-500/25 transition-all hover:bg-amber-400 hover:shadow-amber-400/40 hover:-translate-y-0.5"
        >
          <Phone className="h-4 w-4" /> {BRAND.phone}
        </a>
        <a
          href={BRAND.emailHref}
          data-testid="cta-email-button"
          className="inline-flex items-center justify-center gap-2.5 rounded-full border border-slate-600 bg-slate-800/60 px-7 py-3.5 text-sm font-semibold text-slate-100 transition-all hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5"
        >
          <Mail className="h-4 w-4" /> {BRAND.email}
        </a>
      </div>
    </div>
  </section>
);

export const Faq = ({ items, heading = "Frequently Asked Questions" }) => (
  <section data-testid="faq-section" className="my-14">
    <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mb-6">{heading}</h2>
    <Accordion type="single" collapsible className="w-full space-y-3">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-slate-800 bg-[#111827] px-5 data-[state=open]:border-amber-500/40">
          <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm sm:text-base font-semibold text-slate-200 hover:text-amber-400 hover:no-underline py-4">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-slate-400 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const LinkGrid = ({ title, links, testid }) => (
  <section className="my-14" data-testid={testid}>
    <h2 className="text-xl sm:text-2xl font-bold font-display text-slate-50 tracking-tight mb-5">{title}</h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {links.map((l) => (
        <Link
          key={l.path}
          to={l.path}
          data-testid={`${testid}-link`}
          className="rounded-lg border border-slate-800 bg-[#111827] px-4 py-3 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-400 hover:-translate-y-0.5"
        >
          {l.label}
        </Link>
      ))}
    </div>
  </section>
);
